import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { Modal, Box, Typography, Slider, Button, IconButton } from "@mui/material";
import { style, header } from "./modalStyles";
import FormField from "../FormField";
import { halfHourTimeOptions } from "../Time/timeOptions";
import ChargeBetween from "../Time/ChargeBetween";
import { LoadingButton } from "@mui/lab";

dayjs.extend(duration);

const MODAL_DATA = {
  boost: {
    title: "One-Time Boost Charge",
    description:
      "Start a one-off boost charge to attempt to immediately charge your vehicle for a choosen time duration.",
    buttonName: "Start Charge",
  },
  boosting: {
    title: "Stop Boost Charge",
    description:
      "One off-boost charge to attempt to immediately charge your vehicle for a choosen time duration.",
    buttonName: "Stop Boost Charge",
  },
  schedule: {
    title: "Top-up Schedule",
    description:
      "Choose a time winodw to always attempt to charge your vehicle, either from solar if available or the electricity grid.",
    buttonName: "Save",
  },
};

const CHARGE_OPTIONS = [
  { value: 0.5, label: "30mins" },
  { value: 1 },
  { value: 2 },
  { value: 3 },
  { value: 4 },
  { value: 5 },
  { value: 6 },
  { value: 7 },
  { value: 8 },
  { value: 9 },
  { value: 10 },
  { value: 11 },
  { value: 12, label: "12hrs" },
  { value: 13 },
  { value: 14 },
  { value: 15 },
  { value: 16 },
  { value: 17 },
  { value: 18 },
  { value: 19 },
  { value: 20 },
  { value: 21 },
  { value: 22 },
  { value: 23 },
  { value: 24, label: "24hrs" },
];

const styles = () => {
  return {
    "& .time-fields": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      "& .mui-field": {
        marginRight: "12px",
        width: "48%",
        "& .mui-field-label": {
          minWidth: "40%",
          paddingRight: "4px",
          justifyContent: "end",
        },
      },
    },
  };
};

const EvBoostModal = ({
  chargeBoost,
  open,
  onClose,
  onSaveBoost,
  boostState = "boost",
  loading = false,
  chargeFormData,
}) => {
  const [chargeDuration, setChargeDuration] = useState(0.5);
  const [modalData, setModalData] = useState(MODAL_DATA["boost"]);

  const [target, setTarget] = useState(new Date());
  const [formData, setFormData] = useState({
    start: 0,
    end: 1,
  });

  useEffect(() => {
    setModalData(MODAL_DATA[boostState]);
  }, [boostState]);

  useEffect(() => {
    if (chargeBoost && boostState === "boosting") {
      setTarget(chargeBoost);
    }
  }, [chargeBoost]);

  useEffect(() => {
    if (boostState === "boost") {
      const target = dayjs(new Date()).add(chargeDuration, "hour").toDate();
      setTarget(target);
    }
  }, [chargeDuration]);

  useEffect(() => {
    if (chargeFormData) {
      setFormData(chargeFormData);
    }
  }, [chargeFormData]);

  const getUntil = () => {
    const targetDate = dayjs(startOfMinute(target));
    const now = dayjs().startOf("day");

    let dayLabel;
    if (targetDate.isSame(now, "day")) {
      dayLabel = "Today";
    } else if (targetDate.isSame(now.add(1, "day"), "day")) {
      dayLabel = "Tomorrow";
    } else {
      dayLabel = targetDate.format("dddd");
    }

    return `${targetDate.format("h:mm A")} | ${dayLabel}`;
  };

  const getRemaining = () => {
    const diff = dayjs(startOfMinute(target)).diff(startOfMinute(new Date()));
    const remaining = dayjs.duration(diff);

    let hours = remaining.hours();
    const minutes = remaining.minutes();

    if (chargeDuration >= 24) {
      hours = hours + 24;
    }

    return `${hours} hrs ${minutes} mins`;
  };

  const startOfMinute = (value) => {
    return dayjs(value).startOf("minute").toDate();
  };

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          ...style,
          width: { xs: "97%", md: "500px" },
          ...styles(),
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h5">{modalData.title}</Typography>
          <Box sx={header}>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Box mt={2}>
          <Typography variant="body2">{modalData.description}</Typography>
          <Box>
            {boostState === "boost" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  my: 3,
                }}
              >
                <Box width={"100%"}>
                  <Typography variant="subtitle2" mb={1}>
                    Charge Duration
                  </Typography>
                  <Slider
                    step={null}
                    min={0.5}
                    max={24}
                    marks={CHARGE_OPTIONS}
                    value={chargeDuration}
                    onChange={(e) => setChargeDuration(e.target.value)}
                    sx={{
                      ml: 2,
                      width: "80%",
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: "center", width: "130px" }}>
                  <Typography variant="body2">{getRemaining()}</Typography>
                  <Typography variant="body2" fontSize="13px">
                    until {getUntil()}
                  </Typography>
                </Box>
              </Box>
            )}

            {boostState === "boosting" && (
              <Box my={3}>
                <Typography variant="body1">
                  Charging until {getUntil()} (
                  <span style={{ fontSize: "13px" }}>
                    {getRemaining()} remaining
                  </span>
                  )
                </Typography>
              </Box>
            )}

            {boostState === "schedule" && (
              <Box my={3}>
                <Box className="time-fields">
                  <FormField
                    showLabel={true}
                    formFieldLabel="Charge From"
                    name="start"
                    value={formData.start}
                    type="select"
                    variant="outlined"
                    onChange={onChange}
                    options={halfHourTimeOptions}
                  />

                  <FormField
                    showLabel={true}
                    formFieldLabel="To"
                    name="end"
                    value={formData.end}
                    type="select"
                    variant="outlined"
                    onChange={onChange}
                    options={halfHourTimeOptions}
                  />
                </Box>

                <ChargeBetween start={formData.start} end={formData.end} />
              </Box>
            )}

            <Box
              pt={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <Button variant="outlined" sx={{ mr: 1 }} onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton
                loading={loading}
                variant="contained"
                onClick={() => onSaveBoost({ target, formData })}
              >
                {modalData.buttonName}
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EvBoostModal;
